import { type ReactNode } from "react";
import { useDialog } from "@/hooks/useDialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type ConfirmVariant = "danger" | "primary";

type ConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Remix Icon class (e.g. "ri-delete-bin-line"). 새 코드는 icon 사용 권장. */
  iconClass?: string;
  /** Lucide 등 React 아이콘 노드 (iconClass보다 우선) */
  icon?: ReactNode;
  title: string;
  description: ReactNode;
  cancelLabel: string;
  confirmLabel: string;
  variant?: ConfirmVariant;
  loading?: boolean;
};

const iconBgClass: Record<ConfirmVariant, string> = {
  danger: "bg-destructive/10 text-destructive",
  primary: "bg-primary/10 text-primary",
};

export default function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  iconClass,
  icon,
  title,
  description,
  cancelLabel,
  confirmLabel,
  variant = "danger",
  loading = false,
}: ConfirmModalProps) {
  const ref = useDialog(isOpen, onClose);
  if (!isOpen) return null;

  const titleId = "confirm-modal-title";
  const descId = "confirm-modal-desc";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center safe-x safe-bottom">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        tabIndex={-1}
        className="relative mx-4 w-full max-w-sm animate-in fade-in zoom-in rounded-2xl border border-border bg-card p-6 text-card-foreground shadow-2xl duration-200 focus:outline-none"
      >
        <div
          className={cn(
            "mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full",
            iconBgClass[variant]
          )}
          aria-hidden="true"
        >
          {icon ?? (iconClass ? <i className={cn("text-2xl", iconClass)} /> : null)}
        </div>
        <h3 id={titleId} className="mb-2 text-center text-lg font-bold">
          {title}
        </h3>
        <p id={descId} className="mb-6 text-center text-sm text-muted-foreground">
          {description}
        </p>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="flex-1"
            onClick={onClose}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={variant === "danger" ? "destructive" : "default"}
            size="lg"
            className="flex-1"
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
